import { AUTO_ACCEPT_ODDS_CHANGE, SHOW_TOTAL_STAKES_TOGGLE } from '../../data-types/betting/bettingEnums';
import {
  OutcomeSelectionByEventChanged,
  PendingBet,
  SystemBetConfigSettings,
  SystemBetStakeDetails,
  SystemBetTypeExtended,
} from '../../data-types/betting/bettingInterfaces';
import { BooleanMap, StringKeyBooleanValuePair } from '../../data-types/interfaces';
import { TrendingBet } from '../../data-types/sports/sportsInterfaces';
import { SuperchargedBetInfo } from '../../data-types/supercharged-bet/superchargedBetInterfaces';
import { ResetState } from './stateTypes';

export type BetActionType =
  | ResetState
  | SetPendingBets
  | SetPlacingBet
  | SetBetslipSnackbar
  | SetTrendingBets
  | SetTrendingBetsCarouselOpen
  | SetAutoAcceptOddsChange
  | EnqueueSuperchargedBet
  | DequeueSuperchargedBet
  | SetSuperchargedBets
  | SetAllStakesStatus
  | SetSystemBetConfig
  | SetSystemBetStakeDetails
  | RemoveSystemBetStakeDetails
  | RemoveAllSystemBetStakeDetails
  | SetAvailableBetTypes
  | SetSystemBetPlacingOrder
  | AppendSystemBetStakeDetailsArray
  | SetSystemBetStakeValidationStatus
  | RemoveSystemBetStakeValidationStatus
  | SetOutcomeSelectionByEventChanged
  | RemoveOutcomeSelectionByEventChanged
  | RemoveAllOutcomeSelectionByEventChanged;

export const SET_PENDING_BETS = 'SET_PENDING_BETS';
export const SET_IS_PLACING_BET = 'SET_IS_PLACING_BET';
export const SET_BETSLIP_SNACKBAR = 'SET_BETSLIP_SNACKBAR';
export const SET_TRENDING_BETS = 'SET_TRENDING_BETS';
export const SET_TRENDING_BETS_CAROUSEL_OPEN = 'SET_TRENDING_BETS_CAROUSEL_OPEN';
export const SET_AUTO_ACCEPT_ODDS_CHANGE = 'SET_AUTO_ACCEPT_ODDS_CHANGE';
export const ENQUEUE_SUPERCHARGED_BET = 'ENQUEUE_SUPERCHARGED_BET';
export const DEQUEUE_SUPERCHARGED_BET = 'DEQUEUE_SUPERCHARGED_BET';
export const SET_SUPERCHARGED_BETS = 'SET_SUPERCHARGED_BETS';
export const SET_ALL_STAKES_STATUS = 'SET_ALL_STAKES_STATUS';
export const SET_SYSTEM_BET_CONFIG = 'SET_SYSTEM_BET_CONFIG';
export const SET_SYSTEM_BET_STAKE_DETAILS = 'SET_SYSTEM_BET_STAKE_DETAILS';
export const REMOVE_SYSTEM_BET_STAKE_DETAILS = 'REMOVE_SYSTEM_BET_STAKE_DETAILS';
export const APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY = 'APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY';
export const REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS = 'REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS';
export const SET_AVAILABLE_SYSTEM_BET_TYPES = 'SET_AVAILABLE_SYSTEM_BET_TYPES';
export const SET_SYSTEM_BET_PLACING_ORDER = 'SET_SYSTEM_BET_PLACING_ORDER';
export const SET_SYSTEM_BET_STAKE_VALIDATION_STATUS = 'SET_SYSTEM_BET_STAKE_VALIDATION_STATUS';
export const REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS = 'REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS';
export const SET_OUTCOME_SELECTION_BY_EVENT_CHANGES = 'SET_OUTCOME_SELECTION_BY_EVENT_CHANGES';
export const REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES = 'REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES';
export const REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES = 'REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES';

export interface BetState {
  pendingBets: PendingBet[];
  isPlacingBet: boolean;
  betslipSnackbarOpen: boolean;
  trendingBets: TrendingBet[];
  trendingBetsCarouselOpen: boolean;
  autoAcceptOddsChange: AUTO_ACCEPT_ODDS_CHANGE;
  superchargedBets: SuperchargedBetInfo[];
  showTotalStakesToggleStatus: SHOW_TOTAL_STAKES_TOGGLE;
  systemBetConfig: SystemBetConfigSettings | undefined;
  systemBetStakesDetails: SystemBetStakeDetails[];
  availableSystemBetTypes: SystemBetTypeExtended[];
  systemBetsPlacingOrder: SystemBetStakeDetails[];
  systemBetStakeValidationStatus: BooleanMap;
  outcomeSelectionByEventChanged: OutcomeSelectionByEventChanged;
}

interface SetPendingBets {
  type: typeof SET_PENDING_BETS;
  meta: PendingBet[];
}

interface SetPlacingBet {
  type: typeof SET_IS_PLACING_BET;
  meta: boolean;
}

interface SetBetslipSnackbar {
  type: typeof SET_BETSLIP_SNACKBAR;
  meta: boolean;
}

interface SetTrendingBets {
  type: typeof SET_TRENDING_BETS;
  meta: TrendingBet[];
}

interface SetTrendingBetsCarouselOpen {
  type: typeof SET_TRENDING_BETS_CAROUSEL_OPEN;
  meta: boolean;
}

interface SetAutoAcceptOddsChange {
  type: typeof SET_AUTO_ACCEPT_ODDS_CHANGE;
  meta: AUTO_ACCEPT_ODDS_CHANGE;
}

interface EnqueueSuperchargedBet {
  type: typeof ENQUEUE_SUPERCHARGED_BET;
  meta: SuperchargedBetInfo;
}

interface DequeueSuperchargedBet {
  type: typeof DEQUEUE_SUPERCHARGED_BET;
}

interface SetSuperchargedBets {
  type: typeof SET_SUPERCHARGED_BETS;
  meta: SuperchargedBetInfo[];
}

interface SetAllStakesStatus {
  type: typeof SET_ALL_STAKES_STATUS;
  meta: SHOW_TOTAL_STAKES_TOGGLE;
}

interface SetSystemBetConfig {
  type: typeof SET_SYSTEM_BET_CONFIG;
  meta: SystemBetConfigSettings;
}

interface SetSystemBetStakeDetails {
  type: typeof SET_SYSTEM_BET_STAKE_DETAILS;
  meta: SystemBetStakeDetails;
}

interface RemoveSystemBetStakeDetails {
  type: typeof REMOVE_SYSTEM_BET_STAKE_DETAILS;
  meta: number;
}

interface AppendSystemBetStakeDetailsArray {
  type: typeof APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY;
  meta: SystemBetStakeDetails[];
}

interface RemoveAllSystemBetStakeDetails {
  type: typeof REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS;
  meta: number;
}

interface SetAvailableBetTypes {
  type: typeof SET_AVAILABLE_SYSTEM_BET_TYPES;
  meta: SystemBetTypeExtended[];
}

interface SetSystemBetPlacingOrder {
  type: typeof SET_SYSTEM_BET_PLACING_ORDER;
  meta: SystemBetStakeDetails[];
}

interface SetSystemBetStakeValidationStatus {
  type: typeof SET_SYSTEM_BET_STAKE_VALIDATION_STATUS;
  meta: StringKeyBooleanValuePair;
}

interface RemoveSystemBetStakeValidationStatus {
  type: typeof REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS;
  meta: string;
}

interface SetOutcomeSelectionByEventChanged {
  type: typeof SET_OUTCOME_SELECTION_BY_EVENT_CHANGES;
  meta: OutcomeSelectionByEventChanged;
}

interface RemoveOutcomeSelectionByEventChanged {
  type: typeof REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES;
  meta: string;
}

interface RemoveAllOutcomeSelectionByEventChanged {
  type: typeof REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES;
}
